import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home,
  Building2,
  FileText,
  ShoppingCart,
  DollarSign,
  Users,
  Settings,
  Menu,
  X,
  LogOut,
  ClipboardList,
  ListTree,
  Wallet,
  Loader2,
  Building,
  Cog,
  BarChart3,
  Receipt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { RoleSwitcher } from "@/components/ui/role-switcher";
import { cn } from "@/lib/utils";

interface BackofficeLayoutProps {
  children: React.ReactNode;
}

const BackofficeLayout: React.FC<BackofficeLayoutProps> = ({ children }) => {
  const { user, logout, loading, isAuthenticated, userRoles, selectedRole, hasPermission, getCurrentRoleIds } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!loading && !isAuthenticated) {
      navigate("/login", { state: { from: location } });
    }
  }, [loading, isAuthenticated, navigate, location]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // The useEffect above will handle the redirect
  }

  // Check if currently selected role is a platform role
  const isCurrentlyPlatformRole = selectedRole?.type === 'platform';

  // Get current role IDs for permission checking
  const currentRoleIds = getCurrentRoleIds();

  // Debug logging for menu permissions
  console.log('BackofficeLayout debug:', {
    selectedRole,
    currentRoleIds,
    userRolesPlatform: userRoles.platform,
    hasUsersPermission: hasPermission(currentRoleIds, 'read', 'users'),
  });

  // Base navigation items with their corresponding entity names for permission checking
  const baseNavigationItems = [
    { name: "Dashboard", href: "/backoffice/dashboard", icon: Home, alwaysVisible: true },
    { name: "Associations", href: "/backoffice/associations", icon: Building2, entityName: "associations" },
    // Inside a MC context the cross-company listing makes no sense (8-Sep,
    // Mauro: "definilo vos") — the item points straight at YOUR company,
    // where all its tabs (types, finance, notifications, AI) live.
    selectedRole?.type === "management_company"
      ? {
          name: "Company Settings",
          href: `/backoffice/management-companies/${selectedRole.entityId}/edit`,
          icon: Building,
          entityName: "management_companies",
        }
      : { name: "Management Companies", href: "/backoffice/management-companies", icon: Building, entityName: "management_companies" },
    { name: "Documents", href: "/backoffice/documents", icon: FileText, entityName: "documents" },
    { name: "Pricing", href: "/backoffice/pricing", icon: DollarSign, entityName: "pricing_tables" },
    { name: "Orders", href: "/backoffice/orders", icon: ShoppingCart, entityName: "orders" },
    { name: "Analytics", href: "/backoffice/analytics", icon: BarChart3, entityName: "analytics" },
    { name: "Transactions", href: "/backoffice/transactions", icon: Receipt, entityName: "pricing_tables" },
    // Payouts hidden for now - uncomment when ready
    // { name: "Payouts", href: "/backoffice/payouts", icon: Wallet, entityName: "pricing_tables" },
    { name: "Users", href: "/backoffice/users", icon: Users, entityName: "users" },
  ];

  // Platform-only navigation items
  const platformOnlyNavigationItems = [
    { name: "Content Management", href: "/backoffice/cms", icon: ListTree, platformOnly: true },
    { name: "Settings", href: "/backoffice/settings", icon: Settings, platformOnly: true },
  ];

  // Filter base navigation based on permissions
  const filteredBaseNavigation = baseNavigationItems.filter(item => {
    // Always show dashboard
    if (item.alwaysVisible) {
      return true;
    }
    
    if(item.entityName === 'management_companies') {
      if(selectedRole?.type === 'platform' || selectedRole?.type === "management_company") return true;
      return false;
    }

    // Check if user has read permission for this entity
    if (item.entityName) {
      return hasPermission(currentRoleIds, 'read', item.entityName);
    }
    
    return false;
  });

  // Filter platform-only navigation based on current role type
  const filteredPlatformNavigation = isCurrentlyPlatformRole 
    ? platformOnlyNavigationItems 
    : [];

  // Combine filtered navigation
  const navigation = [...filteredBaseNavigation, ...filteredPlatformNavigation];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user ? `${user.firstName[0]}${user.lastName[0]}` : "?";

  return (
    <div className="h-screen flex overflow-hidden bg-white">
      <div
        className={cn(
          "fixed inset-0 flex z-40 lg:hidden",
          sidebarOpen ? "block" : "hidden"
        )}
      >
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white focus:outline-none">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <Logo color="black" className="h-8 w-auto" />
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "group flex items-center px-2 py-2 text-base font-medium rounded-md",
                    location.pathname === item.href
                      ? "bg-brand-orange text-white"
                      : "text-gray-600 hover:bg-brand-orange/10 hover:text-brand-orange"
                  )}
                >
                  <item.icon className="mr-4 h-6 w-6" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex-shrink-0 border-t border-gray-200">
            {/* Role Switcher for Mobile */}
            <div className="p-4 border-b border-gray-200">
              <RoleSwitcher />
            </div>
            <div className="p-4">
              <Button variant="ghost" className="w-full justify-start" onClick={handleLogout}>
                <LogOut className="mr-3 h-5 w-5" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
            <div className="flex items-center h-16 flex-shrink-0 px-4 bg-white">
              <Logo color="black" className="h-8 w-auto" />
            </div>
            <div className="flex-1 flex flex-col overflow-y-auto">
              <nav className="flex-1 px-2 py-4 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                      location.pathname === item.href
                        ? "bg-brand-orange text-white"
                        : "text-gray-600 hover:bg-brand-orange/10 hover:text-brand-orange"
                    )}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
            {/* Role Switcher for Desktop */}
            <div className="flex-shrink-0 border-t border-gray-200 p-4">
              <RoleSwitcher />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-orange lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-end">
            <div className="ml-4 flex items-center md:ml-6">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative rounded-full">
                    <Avatar>
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user?.firstName} {user?.lastName}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/backoffice/settings" className="flex items-center">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none bg-white">
          <div className="py-6 px-4 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default BackofficeLayout;
